import {
  Button,
  ButtonColor,
  ButtonSize,
  Combobox,
  Option,
  TextInput,
} from '@code/innovance-react-ui-kit';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { BenefitsThirdPartyPageValidation, CustomerThirdParty } from 'src/front/api/apiInstance';
import {
  ReferenceDataType,
  ThirdPartyPageValidationComponentsResponse,
} from 'src/front/api/generated';
import usePRM from 'src/front/common/hooks/usePRM';
import useYupResolver from 'src/front/common/hooks/useYupResolver';
import { convertStringToDate, formatDate } from 'src/front/common/utils/dateTimeUtils';
import {
  dictionaryLineFormat,
  formatAlphaNumeric,
  formatAlphaNumericCyrillicPlusBlank,
  formatAlphaNumericCyrillicPlusDashAndBlank,
  formatFreeText,
  formatMaskedText,
  formatNumeric,
  optionFormat,
} from 'src/front/common/utils/formUtils';
import { Benefits3rdPartySchema } from 'src/front/common/utils/validationSchemas';
import Datepicker from 'src/front/components/Datepicker';

import {
  Container,
  FormElement,
  FormRow,
  Separator,
  Subtitle,
  Title,
} from 'src/front/components/FormStyles';
import { YES_NO } from 'src/front/constant';

const FORMATTER = {
  NUMERIC: formatNumeric,
  ALPHANUMERIC: formatAlphaNumeric,
  FREETEXT: formatFreeText,
};

interface FormData {
  hasBenefitsThirdParty: Option | null;
  beneficiaryName: string;
  birthPlace: string;
  birthDate: Date | null;
  identityType: Option | null;
  identityDocumentSerialCode: string;
  identityDocumentSerialNumber: string;
  issueDate: Date | null;
  issuedBy: string;
  unitCode: string;
  inn: string;
  hasControlPerson: Option | null;
  beneficiaryName2: string;
  birthPlace2: string;
  birthDate2: Date | null;
  identityType2: Option | null;
  identityDocumentSerialCode2: string;
  identityDocumentSerialNumber2: string;
  issueDate2: Date | null;
  issuedBy2: string;
  unitCode2: string;
  inn2: string;
}

const defaultValues = {
  hasBenefitsThirdParty: null,
  beneficiaryName: '',
  birthPlace: '',
  birthDate: null,
  identityType: null,
  identityDocumentSerialCode: '',
  identityDocumentSerialNumber: '',
  issueDate: null,
  issuedBy: '',
  unitCode: '',
  inn: '',
  hasControlPerson: null,
  beneficiaryName2: '',
  birthPlace2: '',
  birthDate2: null,
  identityType2: null,
  identityDocumentSerialCode2: '',
  identityDocumentSerialNumber2: '',
  issueDate2: null,
  issuedBy2: '',
  unitCode2: '',
  inn2: '',
};

interface Props {
  customerId?: string;
  goToNextStep: (customerId?: string) => void;
  disablePage?: boolean;
}

const Benefits3rdParty: FC<Props> = ({ customerId, goToNextStep, disablePage }) => {
  const { t } = useTranslation();

  const identityTypes = usePRM(ReferenceDataType.IDENTITYTYPE);
  const yesNoComboItems = usePRM(ReferenceDataType.YESNOTYPE);

  const [formValidations, setFormValidations] = useState<
    ThirdPartyPageValidationComponentsResponse[]
  >([]);

  const resolver = useYupResolver(
    Benefits3rdPartySchema,
    {
      todayMaxControl: true,
    },
    { formValidations }
  );

  const { handleSubmit, errors, setValue, register, watch, control, formState } = useForm<FormData>(
    {
      defaultValues,
      resolver,
    }
  );

  const getFormValidations = useCallback(
    async (
      order: string,
      identityType: string,
      pageData?: ThirdPartyPageValidationComponentsResponse[]
    ): Promise<void> => {
      if (identityType) {
        const { data } = await CustomerThirdParty.getThirdPartyPageValidationParameters(
          identityType
        );
        if (data.result === 'OK') {
          const suffix: string = order === 'identityType' ? '' : '2';
          [
            ...(data.series.enabled
              ? []
              : [{ name: `identityDocumentSerialCode${suffix}`, value: '' }]),
            ...(data.documentNumber.enabled
              ? []
              : [{ name: `identityDocumentSerialNumber${suffix}`, value: '' }]),
            ...(data.issueDate.enabled ? [] : [{ name: `issueDate${suffix}`, value: null }]),
            ...(data.issuedBy.enabled ? [] : [{ name: `issuedBy${suffix}`, value: '' }]),
            ...(data.inn?.enabled ? [] : [{ name: `issueCountry${suffix}`, value: null }]),
            ...(data.unitCode.enabled ? [] : [{ name: `unitCode${suffix}`, value: '' }]),
          ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
          if (pageData) {
            // eslint-disable-next-line no-param-reassign
            pageData[order === 'identityType' ? 0 : 1] = data;
            return;
          }
          const newformValidations = [...formValidations];
          newformValidations[order === 'identityType' ? 0 : 1] = data;
          setFormValidations(newformValidations);
        }
      } else {
        setFormValidations([]);
      }
    },
    [formValidations, setFormValidations, setValue]
  );

  const loadFormFields = useCallback(async () => {
    const { data } = await BenefitsThirdPartyPageValidation.getBenefitsThirdPartyPageData();
    const pageValidations: ThirdPartyPageValidationComponentsResponse[] = [];
    if (data.result === 'OK' && data.pageData) {
      const {
        hasBenefitsThirdParty,
        benefitsThirdParty,
        hasControlPerson,
        controlPerson,
      } = data.pageData;
      if (benefitsThirdParty?.identityType.code) {
        await getFormValidations(
          'identityType',
          benefitsThirdParty.identityType.code,
          pageValidations
        );
      }
      if (controlPerson?.identityType.code) {
        await getFormValidations('identityType2', controlPerson.identityType.code, pageValidations);
      }
      setFormValidations(pageValidations);
      [
        { name: 'hasBenefitsThirdParty', value: optionFormat(hasBenefitsThirdParty) },
        { name: 'beneficiaryName', value: benefitsThirdParty?.beneficiaryName || '' },
        { name: 'birthPlace', value: benefitsThirdParty?.birthPlace || '' },
        { name: 'birthDate', value: convertStringToDate(benefitsThirdParty?.birthDate) },
        {
          name: 'identityType',
          value: benefitsThirdParty?.identityType
            ? optionFormat(benefitsThirdParty?.identityType)
            : null,
        },
        {
          name: 'identityDocumentSerialCode',
          value: benefitsThirdParty?.identityDocumentSerialCode || '',
        },
        {
          name: 'identityDocumentSerialNumber',
          value: benefitsThirdParty?.identityDocumentSerialNumber || '',
        },
        { name: 'issueDate', value: convertStringToDate(benefitsThirdParty?.issueDate) },
        { name: 'issuedBy', value: benefitsThirdParty?.issuedBy || '' },
        { name: 'unitCode', value: benefitsThirdParty?.unitCode || '' },
        { name: 'inn', value: benefitsThirdParty?.inn || '' },

        { name: 'hasControlPerson', value: optionFormat(hasControlPerson) },
        { name: 'beneficiaryName2', value: controlPerson?.beneficiaryName || '' },
        { name: 'birthPlace2', value: controlPerson?.birthPlace || '' },
        { name: 'birthDate2', value: convertStringToDate(controlPerson?.birthDate) },
        {
          name: 'identityType2',
          value: controlPerson?.identityType ? optionFormat(controlPerson?.identityType) : null,
        },
        {
          name: 'identityDocumentSerialCode2',
          value: controlPerson?.identityDocumentSerialCode || '',
        },
        {
          name: 'identityDocumentSerialNumber2',
          value: controlPerson?.identityDocumentSerialNumber || '',
        },
        { name: 'issueDate2', value: convertStringToDate(controlPerson?.issueDate) },
        { name: 'issuedBy2', value: controlPerson?.issuedBy || '' },
        { name: 'unitCode2', value: controlPerson?.unitCode || '' },
        { name: 'inn2', value: controlPerson?.inn || '' },
      ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
    }
  }, [getFormValidations, setValue]);

  useEffect(() => {
    loadFormFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Eslint should be disabled to not add dependency

  useEffect(() => {
    register({ name: 'beneficiaryName' });
    register({ name: 'birthPlace' });
    register({ name: 'birthDate' });
    register({ name: 'identityType' });
    register({ name: 'identityDocumentSerialCode' });
    register({ name: 'identityDocumentSerialNumber' });
    register({ name: 'issueDate' });
    register({ name: 'issuedBy' });
    register({ name: 'unitCode' });
    register({ name: 'inn' });
    register({ name: 'beneficiaryName2' });
    register({ name: 'birthPlace2' });
    register({ name: 'birthDate2' });
    register({ name: 'identityType2' });
    register({ name: 'identityDocumentSerialCode2' });
    register({ name: 'identityDocumentSerialNumber2' });
    register({ name: 'issueDate2' });
    register({ name: 'issuedBy2' });
    register({ name: 'unitCode2' });
    register({ name: 'inn2' });
  }, [register]);

  const handleTextChange = (fieldName: string, mask?: string) => (text: string) =>
    setValue(fieldName, formatMaskedText(text, mask), {
      shouldValidate: formState.isSubmitted && !!text,
      shouldDirty: formState.isSubmitted || !!text,
    });
  const handleDateChange = (fieldName: string) => (date: Date): void =>
    setValue(fieldName, date, {
      shouldValidate: formState.isSubmitted || !!date,
      shouldDirty: formState.isSubmitted || !!date,
    });
  const handleComboChange = (name: string) => (option: Option | null) => {
    setValue(name, option, { shouldValidate: true, shouldDirty: true });
    getFormValidations(name, option?.value || '');
  };

  const onSubmit = async (form: FormData): Promise<void> => {
    const { identityType, identityType2 } = form;
    const { data } = await BenefitsThirdPartyPageValidation.validateBenefitsThirdPartyPageData({
      hasBenefitsThirdParty: dictionaryLineFormat(form.hasBenefitsThirdParty),
      ...(form.hasBenefitsThirdParty?.value === YES_NO.YES
        ? {
            benefitsThirdParty: {
              beneficiaryName: form.beneficiaryName,
              birthPlace: form.birthPlace,
              birthDate: formatDate(form.birthDate),
              identityType: dictionaryLineFormat(identityType),
              identityDocumentSerialCode: form.identityDocumentSerialCode,
              identityDocumentSerialNumber: form.identityDocumentSerialNumber,
              issueDate: formatDate(form.issueDate),
              issuedBy: form.issuedBy,
              unitCode: form.unitCode,
              inn: form.inn,
            },
          }
        : {}),
      hasControlPerson: dictionaryLineFormat(form.hasControlPerson),
      ...(form.hasControlPerson?.value === YES_NO.YES
        ? {
            controlPerson: {
              beneficiaryName: form.beneficiaryName2,
              birthPlace: form.birthPlace2,
              birthDate: formatDate(form.birthDate2),
              identityType: dictionaryLineFormat(identityType2),
              identityDocumentSerialCode: form.identityDocumentSerialCode2,
              identityDocumentSerialNumber: form.identityDocumentSerialNumber2,
              issueDate: formatDate(form.issueDate2),
              issuedBy: form.issuedBy2,
              unitCode: form.unitCode2,
              inn: form.inn2,
            },
          }
        : {}),
    });
    if (data.result === 'OK') {
      goToNextStep(customerId);
    }
  };

  return (
    <Container>
      <Title>
        <Trans i18nKey="cardEntry_benefit3rdParty_title" />
      </Title>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormRow>
          <Trans i18nKey="cardEntry_benefit3rdParty_actBenefitOf3rdParty_text" />
        </FormRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="hasBenefitsThirdParty"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={disablePage}
                  errorMessage={t((errors?.hasBenefitsThirdParty as FieldError)?.message as string)}
                  items={yesNoComboItems}
                  label={t('cardEntry_benefit3rdParty_doYouConfirm1_label')}
                  name="hasBenefitsThirdParty"
                  onChange={onChange}
                  value={value}
                  required
                />
              )}
            />
          </FormElement>
        </FormRow>
        {watch('hasBenefitsThirdParty')?.value === YES_NO.YES ? (
          <>
            <Subtitle>
              <Trans i18nKey="cardEntry_benefit3rdParty_beneficaryInformation1_text" />
            </Subtitle>
            <FormRow>
              <FormElement>
                <TextInput
                  disabled={disablePage}
                  errorMessage={t(errors?.beneficiaryName?.message as string)}
                  formatter={formatAlphaNumericCyrillicPlusBlank}
                  label={t('cardEntry_benefit3rdParty_beneficaryName1_label')}
                  maxLength={150}
                  name="beneficiaryName"
                  onChange={handleTextChange('beneficiaryName')}
                  value={watch('beneficiaryName')}
                  required
                />
              </FormElement>
            </FormRow>
            <FormRow>
              <FormElement>
                <TextInput
                  disabled={disablePage}
                  errorMessage={t(errors?.birthPlace?.message as string)}
                  formatter={formatAlphaNumericCyrillicPlusDashAndBlank}
                  label={t('cardEntry_benefit3rdParty_placeofBirth1_label')}
                  maxLength={150}
                  name="birthPlace"
                  onChange={handleTextChange('birthPlace')}
                  value={watch('birthPlace')}
                  required
                />
              </FormElement>
              <FormElement>
                <Datepicker
                  disabled={disablePage}
                  errorMessage={t(errors?.birthDate?.message as string)}
                  inputLabel={`${t('cardEntry_benefit3rdParty_dateOfBirth1_label')} *`}
                  onChange={handleDateChange('birthDate')}
                  value={watch('birthDate') as Date}
                />
              </FormElement>
            </FormRow>
            <Subtitle>
              <Trans i18nKey="cardEntry_benefit3rdParty_mainDocumentIdentityInfo1_label" />
            </Subtitle>
            <FormRow>
              <FormElement>
                <Combobox
                  disabled={disablePage}
                  errorMessage={t((errors?.identityType as FieldError)?.message as string)}
                  items={identityTypes}
                  label={t('cardEntry_benefit3rdParty_documentType1_label')}
                  name="identityType"
                  onChange={handleComboChange('identityType')}
                  searchPlaceholder={t('cardEntry_documentEntry_searchDocumentType_text')}
                  value={watch('identityType')}
                  required
                  searchable
                />
              </FormElement>
            </FormRow>
            {formValidations[0] && watch('identityType') ? (
              <>
                <FormRow>
                  <FormElement>
                    <TextInput
                      disabled={!formValidations[0].series.enabled}
                      errorMessage={t(errors?.identityDocumentSerialCode?.message as string, {
                        min: formValidations[0].series.minimumLength,
                      })}
                      formatter={FORMATTER[formValidations[0].series.dataType || 'ALPHANUMERIC']}
                      label={t('cardEntry_benefit3rdParty_series1_label')}
                      maxLength={
                        formValidations[0].series.maskFormat?.length ||
                        formValidations[0].series.maximumLength
                      }
                      name="identityDocumentSerialCode"
                      onChange={handleTextChange(
                        'identityDocumentSerialCode',
                        formValidations[0].series.maskFormat
                      )}
                      required={formValidations[0].series.required}
                      value={watch('identityDocumentSerialCode')}
                    />
                  </FormElement>
                  <FormElement>
                    <TextInput
                      disabled={!formValidations[0].documentNumber.enabled}
                      errorMessage={t(errors?.identityDocumentSerialNumber?.message as string, {
                        min: formValidations[0].documentNumber.minimumLength,
                      })}
                      formatter={
                        FORMATTER[formValidations[0].documentNumber.dataType || 'FREETEXT']
                      }
                      label={t('cardEntry_benefit3rdParty_documentNumber1_label')}
                      maxLength={
                        formValidations[0].documentNumber.maskFormat?.length ||
                        formValidations[0].documentNumber.maximumLength
                      }
                      name="identityDocumentSerialNumber"
                      onChange={handleTextChange(
                        'identityDocumentSerialNumber',
                        formValidations[0].documentNumber.maskFormat
                      )}
                      required={formValidations[0].documentNumber.required}
                      value={watch('identityDocumentSerialNumber')}
                    />
                  </FormElement>
                </FormRow>
                <FormRow>
                  <FormElement>
                    <TextInput
                      disabled={!formValidations[0].issuedBy.enabled}
                      errorMessage={t(errors?.issuedBy?.message as string, {
                        min: formValidations[0].issuedBy.minimumLength,
                      })}
                      formatter={FORMATTER[formValidations[0].issuedBy.dataType || 'FREETEXT']}
                      label={t('cardEntry_benefit3rdParty_issuedBy1_label')}
                      maxLength={
                        formValidations[0].issuedBy.maskFormat?.length ||
                        formValidations[0].issuedBy.maximumLength
                      }
                      name="issuedBy"
                      onChange={handleTextChange(
                        'issuedBy',
                        formValidations[0].issuedBy.maskFormat
                      )}
                      required={formValidations[0].issuedBy.required}
                      value={watch('issuedBy')}
                    />
                  </FormElement>
                  <FormElement>
                    <Datepicker
                      disabled={!formValidations[0].issueDate.enabled}
                      errorMessage={t(errors?.issueDate?.message as string)}
                      inputLabel={`${t('cardEntry_benefit3rdParty_issuedDate1_label')}${
                        formValidations[0].issueDate.required ? ' *' : ''
                      }`}
                      onChange={handleDateChange('issueDate')}
                      value={watch('issueDate') as Date}
                    />
                  </FormElement>
                </FormRow>
                <FormRow>
                  <FormElement>
                    <TextInput
                      disabled={!formValidations[0].unitCode.enabled}
                      errorMessage={t(errors?.unitCode?.message as string, {
                        min: formValidations[0].unitCode.minimumLength,
                      })}
                      formatter={FORMATTER[formValidations[0].unitCode.dataType || 'NUMERIC']}
                      label={t('cardEntry_benefit3rdParty_unitCode1_label')}
                      maxLength={
                        formValidations[0].unitCode.maskFormat?.length ||
                        formValidations[0].unitCode.maximumLength
                      }
                      name="unitCode"
                      onChange={handleTextChange(
                        'unitCode',
                        formValidations[0].unitCode.maskFormat
                      )}
                      required={formValidations[0].unitCode.required}
                      value={watch('unitCode')}
                    />
                  </FormElement>
                  <FormElement>
                    <TextInput
                      disabled={!formValidations[0].inn?.enabled}
                      errorMessage={t(errors?.inn?.message as string, {
                        min: formValidations[0].inn?.minimumLength,
                      })}
                      formatter={FORMATTER[formValidations[0].inn?.dataType || 'NUMERIC']}
                      label={t('cardEntry_benefit3rdParty_inn1_label')}
                      maxLength={
                        formValidations[0].inn?.maskFormat?.length ||
                        formValidations[0].inn?.maximumLength
                      }
                      name="inn"
                      onChange={handleTextChange('inn', formValidations[0].inn?.maskFormat)}
                      required={formValidations[0].inn?.required}
                      value={watch('inn')}
                    />
                  </FormElement>
                </FormRow>
              </>
            ) : null}
          </>
        ) : null}
        <Separator />
        <FormRow>
          <Trans i18nKey="cardEntry_benefit3rdParty_controlYourActions_text" />
        </FormRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="hasControlPerson"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={disablePage}
                  errorMessage={t((errors?.hasControlPerson as FieldError)?.message as string)}
                  items={yesNoComboItems}
                  label={t('cardEntry_benefit3rdParty_doYouConfirm2_label')}
                  name="hasControlPerson"
                  onChange={onChange}
                  value={value}
                  required
                />
              )}
            />
          </FormElement>
        </FormRow>
        {watch('hasControlPerson')?.value === YES_NO.YES ? (
          <>
            <Subtitle>
              <Trans i18nKey="cardEntry_benefit3rdParty_beneficaryInformation2_text" />
            </Subtitle>
            <FormRow>
              <FormElement>
                <TextInput
                  disabled={disablePage}
                  errorMessage={t(errors?.beneficiaryName2?.message as string)}
                  formatter={formatAlphaNumericCyrillicPlusBlank}
                  label={t('cardEntry_benefit3rdParty_beneficaryName2_label')}
                  maxLength={150}
                  name="beneficiaryName2"
                  onChange={handleTextChange('beneficiaryName2')}
                  value={watch('beneficiaryName2')}
                  required
                />
              </FormElement>
            </FormRow>
            <FormRow>
              <FormElement>
                <TextInput
                  disabled={disablePage}
                  errorMessage={t(errors?.birthPlace2?.message as string)}
                  formatter={formatAlphaNumericCyrillicPlusDashAndBlank}
                  label={t('cardEntry_benefit3rdParty_placeofBirth2_label')}
                  maxLength={150}
                  name="birthPlace2"
                  onChange={handleTextChange('birthPlace2')}
                  value={watch('birthPlace2')}
                  required
                />
              </FormElement>
              <FormElement>
                <Datepicker
                  disabled={disablePage}
                  errorMessage={t(errors?.birthDate2?.message as string)}
                  inputLabel={`${t('cardEntry_benefit3rdParty_dateOfBirth2_label')} *`}
                  onChange={handleDateChange('birthDate2')}
                  value={watch('birthDate2') as Date}
                />
              </FormElement>
            </FormRow>
            <Subtitle>
              <Trans i18nKey="cardEntry_benefit3rdParty_mainDocumentIdentityInfo2_label" />
            </Subtitle>
            <FormRow>
              <FormElement>
                <Combobox
                  disabled={disablePage}
                  errorMessage={t((errors?.identityType2 as FieldError)?.message as string)}
                  items={identityTypes}
                  label={t('cardEntry_benefit3rdParty_documentType2_label')}
                  name="identityType2"
                  onChange={handleComboChange('identityType2')}
                  searchPlaceholder={t('cardEntry_documentEntry_searchDocumentType_text')}
                  value={watch('identityType2')}
                  required
                  searchable
                />
              </FormElement>
            </FormRow>
            {formValidations[1] && watch('identityType2') ? (
              <>
                <FormRow>
                  <FormElement>
                    <TextInput
                      disabled={!formValidations[1].series.enabled}
                      errorMessage={t(errors?.identityDocumentSerialCode2?.message as string, {
                        min: formValidations[1].series.minimumLength,
                      })}
                      formatter={FORMATTER[formValidations[1].series.dataType || 'ALPHANUMERIC']}
                      label={t('cardEntry_benefit3rdParty_series2_label')}
                      maxLength={
                        formValidations[1].series.maskFormat?.length ||
                        formValidations[1].series.maximumLength
                      }
                      name="identityDocumentSerialCode2"
                      onChange={handleTextChange(
                        'identityDocumentSerialCode2',
                        formValidations[1].series.maskFormat
                      )}
                      required={formValidations[1].series.required}
                      value={watch('identityDocumentSerialCode2')}
                    />
                  </FormElement>
                  <FormElement>
                    <TextInput
                      disabled={!formValidations[1].documentNumber.enabled}
                      errorMessage={t(errors?.identityDocumentSerialNumber2?.message as string, {
                        min: formValidations[1].documentNumber.minimumLength,
                      })}
                      formatter={
                        FORMATTER[formValidations[1].documentNumber.dataType || 'FREETEXT']
                      }
                      label={t('cardEntry_benefit3rdParty_documentNumber2_label')}
                      maxLength={
                        formValidations[1].documentNumber.maskFormat?.length ||
                        formValidations[1].documentNumber.maximumLength
                      }
                      name="identityDocumentSerialNumber2"
                      onChange={handleTextChange(
                        'identityDocumentSerialNumber2',
                        formValidations[1].documentNumber.maskFormat
                      )}
                      required={formValidations[1].documentNumber.required}
                      value={watch('identityDocumentSerialNumber2')}
                    />
                  </FormElement>
                </FormRow>
                <FormRow>
                  <FormElement>
                    <TextInput
                      disabled={!formValidations[1].issuedBy.enabled}
                      errorMessage={t(errors?.issuedBy2?.message as string, {
                        min: formValidations[1].issuedBy.minimumLength,
                      })}
                      formatter={FORMATTER[formValidations[1].issuedBy.dataType || 'FREETEXT']}
                      label={t('cardEntry_benefit3rdParty_issuedBy2_label')}
                      maxLength={
                        formValidations[1].issuedBy.maskFormat?.length ||
                        formValidations[1].issuedBy.maximumLength
                      }
                      name="issuedBy2"
                      onChange={handleTextChange(
                        'issuedBy2',
                        formValidations[1].issuedBy.maskFormat
                      )}
                      required={formValidations[1].issuedBy.required}
                      value={watch('issuedBy2')}
                    />
                  </FormElement>
                  <FormElement>
                    <Datepicker
                      disabled={!formValidations[1].issueDate.enabled}
                      errorMessage={t(errors?.issueDate2?.message as string)}
                      inputLabel={`${t('cardEntry_benefit3rdParty_issuedDate2_label')}${
                        formValidations[1].issueDate.required ? ' *' : ''
                      }`}
                      onChange={handleDateChange('issueDate2')}
                      value={watch('issueDate2') as Date}
                    />
                  </FormElement>
                </FormRow>
                <FormRow>
                  <FormElement>
                    <TextInput
                      disabled={!formValidations[1].unitCode.enabled}
                      errorMessage={t(errors?.unitCode2?.message as string, {
                        min: formValidations[1].unitCode.minimumLength,
                      })}
                      formatter={FORMATTER[formValidations[1].unitCode.dataType || 'NUMERIC']}
                      label={t('cardEntry_benefit3rdParty_unitCode2_label')}
                      maxLength={
                        formValidations[1].unitCode.maskFormat?.length ||
                        formValidations[1].unitCode.maximumLength
                      }
                      name="unitCode2"
                      onChange={handleTextChange(
                        'unitCode2',
                        formValidations[1].unitCode.maskFormat
                      )}
                      required={formValidations[1].unitCode.required}
                      value={watch('unitCode2')}
                    />
                  </FormElement>
                  <FormElement>
                    <TextInput
                      disabled={!formValidations[1].inn?.enabled}
                      errorMessage={t(errors?.inn?.message as string, {
                        min: formValidations[1].inn?.minimumLength,
                      })}
                      formatter={FORMATTER[formValidations[1].inn?.dataType || 'NUMERIC']}
                      label={t('cardEntry_benefit3rdParty_inn2_label')}
                      maxLength={
                        formValidations[1].inn?.maskFormat?.length ||
                        formValidations[1].inn?.maximumLength
                      }
                      name="inn2"
                      onChange={handleTextChange('inn2', formValidations[1].inn?.maskFormat)}
                      required={formValidations[1].inn?.required}
                      value={watch('inn2')}
                    />
                  </FormElement>
                </FormRow>
              </>
            ) : null}
          </>
        ) : null}
        <FormRow>
          <Button
            color={ButtonColor.Primary}
            disabled={formState.isSubmitting}
            size={ButtonSize.Medium}
            text={t('general_continue_button')}
            type="submit"
          />
        </FormRow>
      </form>
    </Container>
  );
};

export default Benefits3rdParty;
